(() => {
  'use strict';

  const root = document.querySelector('[data-ccg-search-root]');
  if (!root) return;

  const masterUrl = root.dataset.masterUrl;
  const shardUrls = (root.dataset.shardUrls || '')
    .split('|')
    .filter(Boolean);

  const els = {
    form: root.querySelector('[data-ccg-search-form]'),
    input: root.querySelector('[data-ccg-search-input]'),
    status: root.querySelector('[data-ccg-search-status]'),
    count: root.querySelector('[data-ccg-search-count]'),
    filters: root.querySelector('[data-ccg-search-filters]'),
    results: root.querySelector('[data-ccg-search-results]'),
    pagination: root.querySelector('[data-ccg-search-pagination]'),
    clear: root.querySelector('[data-ccg-search-clear]')
  };

  const PAGE_SIZE = 48;

  const state = {
    master: null,
    products: [],
    query: '',
    page: 1,

    selected: {
      game_type: new Set(),
      school_level: new Set(),
      grade_subject: new Set(),
      unit: new Set(),
      topic: new Set()
    }
  };

  const fieldIndex = {
    school_level: 3,
    grade_subject: 4,
    unit: 5,
    topic: 6,
    game_type: 7
  };

  const dictKey = {
    school_level: 'school_level',
    grade_subject: 'grade_subject',
    unit: 'unit',
    topic: 'topic',
    game_type: 'game_type'
  };

  const facetLabels = {
    game_type: 'Product Type',
    school_level: 'School Level',
    grade_subject: 'Grade & Subject',
    unit: 'Unit',
    topic: 'Topic'
  };


  // Normalize search words and ordinal numbers.
  // Examples: 7th -> 7, 3rd -> 3, 11th -> 11.
  
  function searchTokens(value) {
    const words = String(value ?? '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .match(/[a-z0-9]+/g) || [];

    const irregularPlurals = {
      children: 'child',
      people: 'person',
      men: 'man',
      women: 'woman',
      mice: 'mouse',
      geese: 'goose',
      teeth: 'tooth',
      feet: 'foot',
      lives: 'life',
      leaves: 'leaf',
      knives: 'knife',
      wolves: 'wolf',
      halves: 'half',
      analyses: 'analysis',
      crises: 'crisis'
    };

    return words.map((word) => {
      // Normalize ordinal numbers: 7th -> 7, 3rd -> 3
      word = word.replace(/^(\d+)(st|nd|rd|th)$/, '$1');

      // Preserve numbers
      if (/^\d+$/.test(word)) return word;

      // Irregular plurals
      if (irregularPlurals[word]) {
        return irregularPlurals[word];
      }

      // consonant + ies: categories -> category
      if (word.length > 4 && /[^aeiou]ies$/.test(word)) {
        return word.slice(0, -3) + 'y';
      }

      // boxes -> box, classes -> class, quizzes -> quiz
      if (word.length > 4 && /(ches|shes|sses|xes|zzes)$/.test(word)) {
        return word.slice(0, -2);
      }

      // games -> game, periods -> period
      if (
        word.length > 3 &&
        word.endsWith('s') &&
        !word.endsWith('ss') &&
        !word.endsWith('us') &&
        !word.endsWith('is')
      ) {
        return word.slice(0, -1);
      }

      return word;
    });
  }


  // Cache searchable product words for performance.
  const productSearchCache = new WeakMap();

  let cachedQuery = null;
  let cachedQueryTokens = [];

  function getProductSearchTokens(product) {
    if (productSearchCache.has(product)) {
      return productSearchCache.get(product);
    }

    const searchable = [product[2]];

    for (const facet of URL_FACETS) {
      const dictionary = getDictionary(facet);

      searchable.push(
        dictionary[product[fieldIndex[facet]]] || ''
      );
    }

    const tokens = searchTokens(searchable.join(' '));

    productSearchCache.set(product, tokens);

    return tokens;
  }


  const URL_FACETS = [
    'game_type',
    'school_level',
    'grade_subject',
    'unit',
    'topic'
    ];

    function restoreStateFromUrl() {
    const params = new URLSearchParams(window.location.search);

    state.query = params.get('q') || '';

    for (const facet of URL_FACETS) {
        state.selected[facet].clear();

        const dictionary = getDictionary(facet);
        const urlValues = params.getAll(facet);

        for (const value of urlValues) {
        const index = dictionary.findIndex(
            (label) => label === value
        );

        if (index !== -1) {
            state.selected[facet].add(String(index));
        }
        }
    }

    const requestedPage =
        parseInt(params.get('page') || '1', 10);

    state.page =
        Number.isFinite(requestedPage) &&
        requestedPage > 0
        ? requestedPage
        : 1;
    }

    function updateUrlState() {
    const url = new URL(window.location.href);

    url.searchParams.delete('q');
    url.searchParams.delete('page');

    for (const facet of URL_FACETS) {
        url.searchParams.delete(facet);
    }

    if (state.query) {
        url.searchParams.set('q', state.query);
    }

    for (const facet of URL_FACETS) {
        const dictionary = getDictionary(facet);

        const selectedValues =
        [...state.selected[facet]]
            .map(Number)
            .sort((a, b) => a - b);

        for (const index of selectedValues) {
        const label = dictionary[index];

        if (label !== undefined) {
            url.searchParams.append(
            facet,
            label
            );
        }
        }
    }

    if (state.page > 1) {
        url.searchParams.set(
        'page',
        String(state.page)
        );
    }

    history.replaceState(
        {},
        '',
        url
    );
    }


  function getProductMatchScore(product) {
    if (!state.query.trim()) return 0;

    if (cachedQuery !== state.query) {
      cachedQuery = state.query;
      cachedQueryTokens = [...new Set(searchTokens(state.query))];
    }

    const productTokens = getProductSearchTokens(product);

    let score = 0;

    for (const term of cachedQueryTokens) {
      const matches = productTokens.some((word) =>
        /^\d+$/.test(term)
          ? word === term
          : word === term
      );

      if (matches) score++;
    }

    return score;
  }

  function productMatchesQuery(product) {
    return !state.query.trim() || getProductMatchScore(product) > 0;
  }


  function productMatchesFilters(product, omitFacet = null) {
    for (const [facet, selected] of Object.entries(state.selected)) {

      if (facet === omitFacet || selected.size === 0) {
        continue;
      }

      if (!selected.has(String(product[fieldIndex[facet]]))) {
        return false;
      }
    }

    return true;
  }

  function getCurrentUniverse() {
    const universe = state.products.filter(
      (product) =>
        productMatchesQuery(product) &&
        productMatchesFilters(product)
    );

    if (!state.query.trim()) {
      return universe;
    }

    // Group products by number of matching search words.
    const groups = new Map();

    for (const product of universe) {
      const score = getProductMatchScore(product);

      if (!groups.has(score)) {
        groups.set(score, []);
      }

      groups.get(score).push(product);
    }

    // Highest relevance first, while rotating Game Types
    // independently inside each relevance group.
    const scores = [...groups.keys()].sort((a, b) => b - a);

    return scores.flatMap((score) =>
      rotateByGameType(groups.get(score))
    );
  }

  function getUniverseExcludingFacet(facet) {
    return state.products.filter(
      (product) =>
        productMatchesQuery(product) &&
        productMatchesFilters(product, facet)
    );
  }

  function getDictionary(facet) {
    const raw =
      state.master.dictionaries[dictKey[facet]] || [];

    if (facet === 'game_type') {
      return raw.map((item) => item.label);
    }

    return raw;
  }

  function countFacetValues(facet) {
    const counts = new Map();
    const index = fieldIndex[facet];

    for (const product of getUniverseExcludingFacet(facet)) {
      const key = String(product[index]);

      counts.set(
        key,
        (counts.get(key) || 0) + 1
      );
    }

    return counts;
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function money(price, currency) {
    const number = Number(price);

    if (!Number.isFinite(number)) {
      return escapeHtml(price);
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(number);
    } catch {
      return `${number.toFixed(2)} ${escapeHtml(currency || '')}`.trim();
    }
  }

  function renderFilters() {
    const order = [
      'game_type',
      'school_level',
      'grade_subject',
      'unit',
      'topic'
    ];

    const blocks = [];

    for (const facet of order) {
      const dictionary = getDictionary(facet);
      const counts = countFacetValues(facet);
      const selected = state.selected[facet];

      const items = [];

      for (let i = 0; i < dictionary.length; i++) {
        const count = counts.get(String(i)) || 0;

        if (
          count === 0 &&
          !selected.has(String(i))
        ) {
          continue;
        }

        items.push(`
          <label class="ccg-search__filter-row">

            <input
              type="checkbox"
              data-ccg-filter="${facet}"
              value="${i}"
              ${
                selected.has(String(i))
                  ? 'checked'
                  : ''
              }
            >

            <span>
              ${escapeHtml(dictionary[i])}
            </span>

            <span class="ccg-search__filter-count">
              ${count.toLocaleString()}
            </span>

          </label>
        `);
      }

      if (!items.length) continue;

      blocks.push(`
        <details class="ccg-search__facet">

          <summary>
            ${escapeHtml(facetLabels[facet])}
          </summary>

          <div class="ccg-search__facet-body">
            ${items.join('')}
          </div>

        </details>
      `);
    }

    els.filters.innerHTML = blocks.join('');

    const isMobile = window.matchMedia('(max-width: 989px)').matches;

    els.filters
    .querySelectorAll('.ccg-search__facet')
    .forEach((facet) => {
        facet.open = !isMobile;
    });

    els.filters
      .querySelectorAll('[data-ccg-filter]')
      .forEach((input) => {

        input.addEventListener('change', () => {

          const facet =
            input.dataset.ccgFilter;

          const value =
            input.value;

          if (input.checked) {
            state.selected[facet].add(value);
          } else {
            state.selected[facet].delete(value);
          }

          state.page = 1;

          updateUrlState();

          renderAll();
        });
      });
  }

    function rotateByGameType(universe) {

    if (!universe.length) {
      return universe;
    }

    const selectedGameTypes =
      state.selected.game_type;

    const gameTypeDictionary =
      getDictionary('game_type');

    /*
      If Game Types are selected:
      use only those selected types, in dictionary order.

      If none are selected:
      use all Game Types, in dictionary order.
    */
    const activeGameTypes = [];

    for (let i = 0; i < gameTypeDictionary.length; i++) {

      const key = String(i);

      if (
        selectedGameTypes.size === 0 ||
        selectedGameTypes.has(key)
      ) {
        activeGameTypes.push(key);
      }
    }

    /*
      One selected Game Type means there is nothing
      to interleave. Preserve the existing order.
    */
    if (activeGameTypes.length <= 1) {
      return universe;
    }

    /*
      Create one queue for each active Game Type.
    */
    const buckets = new Map();

    for (const gameType of activeGameTypes) {
      buckets.set(gameType, []);
    }

    /*
      Put products into their Game Type queues.

      Products retain their existing order inside
      each individual Game Type.
    */
    const leftovers = [];

    for (const product of universe) {

      const gameType =
        String(product[fieldIndex.game_type]);

      const bucket =
        buckets.get(gameType);

      if (bucket) {
        bucket.push(product);
      } else {
        /*
          Safety only:
          never silently lose a product if its
          Game Type is somehow outside the dictionary.
        */
        leftovers.push(product);
      }
    }

    /*
      Round-robin:
      take one product from each Game Type,
      then repeat until all queues are empty.
    */
    const rotated = [];

    let position = 0;
    let productsRemaining = true;

    while (productsRemaining) {

      productsRemaining = false;

      for (const gameType of activeGameTypes) {

        const bucket =
          buckets.get(gameType);

        if (
          bucket &&
          position < bucket.length
        ) {
          rotated.push(
            bucket[position]
          );

          productsRemaining = true;
        }
      }

      position++;
    }

    /*
      Append any unexpected products rather than
      ever dropping them from the result set.
    */
    if (leftovers.length) {
      rotated.push(...leftovers);
    }

    return rotated;
  }

   function renderResults(universe) {

    const totalPages = Math.max(
      1,
      Math.ceil(universe.length / PAGE_SIZE)
    );

    if (state.page > totalPages) {
      state.page = totalPages;
    }

    const start =
      (state.page - 1) * PAGE_SIZE;

    const rows =
      universe.slice(
        start,
        start + PAGE_SIZE
      );

    const gameTypes =
        getDictionary('game_type');

    const gradeSubjects =
        getDictionary('grade_subject');

    const topics =
        getDictionary('topic');

    els.results.innerHTML =
        rows.map((product) => {

        const handle = product[1];
        const originalTitle = product[2];

        const gameType =
            gameTypes[product[7]] || '';

        const gradeSubject =
            gradeSubjects[product[4]] || '';

        const topic =
            topics[product[6]] || originalTitle;

        const image = product[8];
        const price = product[9];
        const currency = product[10];

        return `
            <article class="ccg-search__card">

            <a
                class="ccg-search__image-link"
                href="/products/${encodeURIComponent(handle)}"
            >

                ${
                image
                    ? `
                    <img
                        class="ccg-search__image"
                        src="${escapeHtml(image)}"
                        alt="${escapeHtml(originalTitle)}"
                        loading="lazy"
                    >
                    `
                    : `
                    <div
                        class="
                        ccg-search__image
                        ccg-search__image--empty
                        "
                    ></div>
                    `
                }

            </a>

            <div class="ccg-search__card-body">

                <div class="ccg-search__type">
                ${escapeHtml(gameType)}
                </div>

                <a
                class="ccg-search__title"
                href="/products/${encodeURIComponent(handle)}"
                >
                ${escapeHtml(topic)}
                </a>

                <div class="ccg-search__grade-subject">
                ${escapeHtml(gradeSubject)}
                </div>

                <div class="ccg-search__badge">
                ⚡ INSTANT DOWNLOAD
                <span>
                    • DIGITAL PRODUCT
                </span>
                </div>

                <div class="ccg-search__price">
                ${money(price, currency)}
                </div>

            </div>

            </article>
        `;
        }).join('');

        els.results
            .querySelectorAll('.ccg-search__card')
            .forEach((card) => {
                card.addEventListener('click', (event) => {
                if (event.target.closest('a')) return;

                const link = card.querySelector('.ccg-search__title');

                if (link) {
                    window.location.href = link.href;
                }
                });
            });

    renderPagination(totalPages);
    }

  function renderPagination(totalPages) {

    if (totalPages <= 1) {
      els.pagination.innerHTML = '';
      return;
    }

    const current = state.page;

    const pages = new Set([
      1,
      totalPages,
      current - 2,
      current - 1,
      current,
      current + 1,
      current + 2
    ]);

    const valid = [...pages]
      .filter(
        (page) =>
          page >= 1 &&
          page <= totalPages
      )
      .sort((a, b) => a - b);

    const html = [];

    html.push(`
      <button
        type="button"
        data-page="${Math.max(1, current - 1)}"
        ${current === 1 ? 'disabled' : ''}
      >
        Previous
      </button>
    `);

    let last = 0;

    for (const page of valid) {

      if (
        last &&
        page - last > 1
      ) {
        html.push(
          '<span class="ccg-search__ellipsis">…</span>'
        );
      }

      html.push(`
        <button
          type="button"
          data-page="${page}"
          ${
            page === current
              ? 'aria-current="page"'
              : ''
          }
        >
          ${page}
        </button>
      `);

      last = page;
    }

    html.push(`
      <button
        type="button"
        data-page="${Math.min(totalPages, current + 1)}"
        ${
          current === totalPages
            ? 'disabled'
            : ''
        }
      >
        Next
      </button>
    `);

    els.pagination.innerHTML =
      html.join('');

    els.pagination
      .querySelectorAll('[data-page]')
      .forEach((button) => {

        button.addEventListener(
          'click',
          () => {

            state.page =
                Number(
                    button.dataset.page
                );

                updateUrlState();

                renderAll();

            root.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        );
      });
  }

  function renderCount(universe) {
    const count = universe.length;

    els.count.textContent =
      state.query
        ? `${count.toLocaleString()} Review Games for “${state.query}”`
        : `${count.toLocaleString()} Review Games`;
  }

  function renderAll() {
    const universe =
      getCurrentUniverse();

    renderCount(universe);
    renderFilters();
    renderResults(universe);
  }

  function clearAllFilters() {
    Object
      .values(state.selected)
      .forEach(
        (set) => set.clear()
      );

    state.page = 1;

    updateUrlState();

    renderAll();
  }

    let searchTimer;

    els.input.addEventListener('input', () => {
    clearTimeout(searchTimer);

    searchTimer = setTimeout(() => {
        state.query = els.input.value.trim();
        state.page = 1;

        updateUrlState();
        renderAll();
    }, 180);
    });

  async function load() {

    try {

      const mobileLoader =
        root.querySelector('[data-ccg-mobile-loader]');

      const isMobile =
        window.matchMedia('(max-width: 989px)').matches;


      /*
        Force the mobile loading message to appear
        and give the browser time to paint it BEFORE
        catalog loading begins.
      */
      if (mobileLoader) {

        if (isMobile) {

          mobileLoader.classList.remove('is-complete');
          mobileLoader.style.display = 'block';

          await new Promise(resolve => {
            requestAnimationFrame(() => {
              requestAnimationFrame(resolve);
            });
          });

        } else {

          mobileLoader.style.display = 'none';

        }

      }


      els.status.textContent =
        'Loading complete catalog…';


      const masterResponse =
        await fetch(
          masterUrl,
          {
            cache: 'force-cache'
          }
        );


      if (!masterResponse.ok) {

        throw new Error(
          `Master index failed: ${masterResponse.status}`
        );

      }


      state.master =
        await masterResponse.json();


      const shardData =
        await Promise.all(

          shardUrls.map(

            async (url) => {

              const response =
                await fetch(
                  url,
                  {
                    cache: 'force-cache'
                  }
                );


              if (!response.ok) {

                throw new Error(
                  `Product shard failed: ${response.status}`
                );

              }


              return response.json();

            }

          )

        );


      state.products =
        shardData.flat();


      restoreStateFromUrl();


      els.input.value =
        state.query;


      if (
        state.products.length !==
        state.master.all_products_count
      ) {

        console.warn(
          `CCG Search count mismatch. Master=${state.master.all_products_count}, loaded=${state.products.length}`
        );

      }


      els.status.textContent = '';


      renderAll();


      if (mobileLoader && isMobile) {

        /*
          Wait until the first visible product images
          are genuinely loaded before removing the notice.
        */
        const visibleImages =
          Array.from(
            els.results.querySelectorAll(
              'img.ccg-search__image'
            )
          ).slice(0, 6);


        if (visibleImages.length) {

          await Promise.all(

            visibleImages.map((img) => {

              return new Promise((resolve) => {

                if (
                  img.complete &&
                  img.naturalWidth > 0
                ) {
                  resolve();
                  return;
                }

                const finish = () => resolve();

                img.addEventListener(
                  'load',
                  finish,
                  { once: true }
                );

                img.addEventListener(
                  'error',
                  finish,
                  { once: true }
                );

              });

            })

          );

        }


        /*
          Allow the loaded cards to actually paint.
        */
        await new Promise(resolve => {
          requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
          });
        });


        mobileLoader.style.display = 'none';

      }


    } catch (error) {

      console.error(error);

      els.status.textContent =
        'Search could not be loaded. Please refresh the page.';

    }

  }

  els.form.addEventListener(
    'submit',
    (event) => {

      event.preventDefault();

      state.query =
        els.input.value.trim();

      state.page = 1;

      updateUrlState();

      renderAll();
    }
  );

  els.clear.addEventListener(
    'click',
    clearAllFilters
  );

  window.addEventListener(
    'popstate',
    () => {

        if (!state.master) return;

        restoreStateFromUrl();

        els.input.value =
        state.query;

        renderAll();
    }
    );

  load();

})();


document.addEventListener('pointerdown', (event) => {
  if (
    document.activeElement === els.input &&
    !els.input.contains(event.target)
  ) {
    els.input.blur();
  }
});
